import React from 'react';
import { ReportSummaryCard } from '@/components/Raport/RaportHeader/RaportHeader';
import { RaportMainParams } from '@/components/Raport/RaportMainParams/RaportMainParams';
import { CarHistoryTimeline } from '@/components/CarHistoryTimeline/CarHistoryTimeline';
import { DataTable } from '@/components/DataTable/DataTable';
import { Button } from '@/components/Button/Button';
import { SVGIcon } from '@/components/SVGIcon/SVGIcon';
// import SectionTitle from '@/components/Typography/SectionTitle';
import SectionSubtitle from '@/components/Typography/SectionSubtitle';
import { vinStoryReportMockData } from './VinStoryReport.mock';
import { CarHistoryEventType } from '@/components/CarHistoryTimeline/CarHistoryTimeline.types';
import SectionTitle from '@/components/Typography/SectionTitle';
import { BadgeStatus } from '@/components/Badge/Badge';
import { BadgeStatusType } from '@/components/Badge/BadgeTypes';
import { TextSummaryBox } from '@/components/TextSummaryBox/TextSummaryBox';
import { CarDetailsAssessment } from '@/components/CarDetailsAssessment';
import InspectionList from '@/components/InspectionList/InspectionList';
import { useState } from 'react';
import { InspectionItem } from '@/components/InspectionList';

// Konwertowanie danych timeline na format wymagany przez komponent
const timelineEvents = vinStoryReportMockData.timeline.map((event) => ({
  id: event.id,
  date: event.date,
  type: event.type as CarHistoryEventType,
  title: event.title,
  description: event.description,
  details: {
    ownerType: event.details.ownerType as 'individual' | 'company' | undefined,
    voivodeship: event.voivodeship,
    inspectionResult: event.details.inspectionResult as
      | 'positive'
      | 'negative'
      | undefined,
    inspectionNumber: event.details.inspectionNumber,
    mileage: event.details.mileage || undefined,
  },
}));

export const VinStoryReport: React.FC = () => {
  const {
    vehicle,
    report,
    warnings,
    detailedSpecs,
    damageHistory,
    mileageHistory,
    valuation,
    equipment,
  } = vinStoryReportMockData;

  const [expandedSection, setExpandedSection] = useState<string | null>(
    'standard',
  );

  // Konwersja wyposażenia standardowego na format InspectionItem
  const standardEquipmentItems: InspectionItem[] =
    equipment.standardEquipment.map((item) => ({
      id: item.id,
      name: `${item.name}`,
      status: BadgeStatusType.Success,
      badgeCustomText: item.code,
      comments: 'Wyposażenie standardowe',
    }));

  // Konwersja pakietów wyposażenia
  const packageItems = equipment.equipmentPackages.map((pkg) => ({
    id: pkg.id,
    name: pkg.name,
    status: BadgeStatusType.Info,
    comments: `${pkg.price} • ${pkg.items.length} elementów`,
    badgeCustomText: 'PAKIET',
  }));

  // Konwersja wyposażenia dodatkowego
  const additionalEquipmentItems = equipment.additionalEquipment.map(
    (item) => ({
      id: item.id,
      name: `${item.name}`,
      badgeCustomText: item.code,
      status: BadgeStatusType.Warning,
      comments: item.price,
    }),
  );

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Header z przyciskiem pobierania PDF */}
        <div className="py-4 bg-white border-b">
          <div className="container flex justify-between items-center px-4 mx-auto">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Raport historii pojazdu
              </h1>
              <p className="text-gray-600">
                {vehicle.brand} {vehicle.model} - VIN: {vehicle.vin}
              </p>
            </div>
            <Button
              variant="primary"
              icon="download-pdf-icon"
              onClick={() => console.log('Pobierz PDF')}
            >
              Pobierz PDF
            </Button>
          </div>
        </div>

        {/* Nagłówek raportu z galerią i kartą podsumowania */}
        <div className="raport-bg raport-wrapper p-4 pl-[48px]">
          <div className="container px-4 py-8 mx-auto">
            <div className="mb-6">
              <ReportSummaryCard
                vehicleBrand={vehicle.brand}
                vehicleModel={vehicle.model}
                vehicleType={vehicle.bodyType}
                logoUrl={vehicle.logoUrl}
                carImageUrl={vehicle.carImageUrl}
                vinNumber={vehicle.vin}
                orderNumber={report.orderNumber}
                packageType={report.packageType}
                reportDate={report.reportDate}
                pagesCount={report.pagesCount}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-4">
              {Object.entries(report.sections).map(([key, section]) => (
                <div
                  key={key}
                  className="p-4 py-8 text-center bg-white rounded-lg border border-gray-400"
                >
                  <div className="text-xl font-bold text-blue-600">
                    {section.count}
                  </div>
                  <div className="text-sm text-gray-600">{section.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="container px-4 py-8 mx-auto">
          {/* Ostrzeżenia */}
          {warnings.length > 0 && (
            <section className="mb-12">
              <h2 className="mb-6 text-3xl font-bold text-gray-900">
                Ostrzeżenia
              </h2>
              <div className="mt-6 space-y-4">
                {warnings.map((warning) => (
                  <div
                    key={warning.id}
                    className="p-6 bg-red-50 rounded-lg border border-red-200"
                  >
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0">
                        <SVGIcon name="warning" size="md" fillColor="danger" />
                      </div>
                      <div>
                        <h3 className="mb-2 text-lg font-semibold text-red-800">
                          {warning.title}
                        </h3>
                        <p className="text-red-700">{warning.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Specyfikacja pojazdu */}
          <section className="mb-12">
            <SectionTitle
              title="Specyfikacja"
              description="Specyfikacja pojazdu"
              bgWhite
            />
            <div className="mt-6">
              <RaportMainParams
                vehicleData={{
                  brand: vehicle.brand,
                  model: vehicle.model,
                  productionYear: vehicle.productionYear,
                  vin: vehicle.vin,
                  mileage: `${vehicle.mileage} km`,
                  countryOfOrigin: vehicle.countryOfOrigin,
                  fuelType: vehicle.fuelType,
                  engineDisplacement: `${vehicle.engineDisplacement} ccm`,
                  enginePower: vehicle.enginePower,
                  driveType: vehicle.driveType,
                  engineLayout: vehicle.engineLayout,
                  transmission: vehicle.transmission,
                  bodyType: vehicle.bodyType,
                  doorCount: vehicle.doorCount,
                  color: vehicle.color,
                  maxWeight: vehicle.maxWeight,
                }}
              />
            </div>

            <div className="mt-8">
              <SectionSubtitle title="Pełna specyfikacja techniczna" />
              <div className="mt-4">
                <DataTable data={detailedSpecs} />
              </div>
            </div>
          </section>

          {/* Zdjęcia pojazdu */}
          <section className="mb-12">
            <SectionTitle title="Zdjęcia pojazdu" bgWhite />
            <div className="p-8 mt-6 text-center bg-white rounded-lg border border-gray-200">
              <div className="mb-4 text-gray-500">
                <SVGIcon name="info-ra" size="lg" fillColor="secondary" />
              </div>
              <p className="text-gray-600">
                Przeszukaliśmy wiele baz danych jednak nie udało się znaleźć
                zdjęć dla tego pojazdu.
              </p>
            </div>
          </section>

          {/* Oś czasu */}
          <section className="mb-12">
            <SectionTitle title="Oś czasu" bgWhite />
            <div className="p-6 mt-6 bg-white rounded-lg">
              <CarHistoryTimeline events={timelineEvents} />
            </div>
          </section>

          {/* Historia szkód */}
          <section className="mb-12">
            <SectionTitle title="Historia szkód" bgWhite />
            <div className="mt-6 space-y-6">
              {damageHistory.map((damage, index) => (
                <div
                  key={damage.id}
                  className="p-6 bg-white rounded-lg border border-gray-200"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold">
                      Uszkodzenie pojazdu {index + 1} z {damageHistory.length}
                    </h3>
                    <div className="text-sm text-gray-500">{damage.date}</div>
                  </div>

                  {damage.estimatedValue && (
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-700">
                        Szacunkowa wartość szkody:{' '}
                      </span>
                      <span className="text-sm text-gray-900">
                        {damage.estimatedValue}
                      </span>
                    </div>
                  )}

                  {damage.insurer && (
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-700">
                        Ubezpieczyciel:{' '}
                      </span>
                      <span className="text-sm text-gray-900">
                        {damage.insurer}
                      </span>
                    </div>
                  )}

                  <div className="mb-4">
                    <span className="text-sm font-medium text-gray-700">
                      Uszkodzone części:{' '}
                    </span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {damage.damagedParts.map((part, partIndex) => (
                        <BadgeStatus
                          status={BadgeStatusType.Error}
                          key={partIndex}
                          text={part}
                        ></BadgeStatus>
                      ))}
                    </div>
                  </div>

                  {damage.mileage && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        Stan licznika:{' '}
                      </span>
                      <span className="text-sm font-bold text-gray-900">
                        {damage.mileage} km
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Historia przebiegu */}
          <section className="mb-12">
            <SectionTitle title="Historia przebiegu" bgWhite />
            <div className="mt-6">
              <DataTable
                data={mileageHistory.map((entry, index) => ({
                  id: index + 1,
                  label: `${entry.date} - ${entry.source}`,
                  value: `${entry.mileage} km ${entry.warning ? '⚠️' : ''}`,
                }))}
              />
            </div>
          </section>

          {/* Wycena pojazdu */}
          <section className="mb-12">
            <SectionTitle title="Wycena pojazdu" bgWhite />
            <CarDetailsAssessment
              assessmentItems={[
                {
                  id: 'elementy-metalowe',
                  title: 'Szacowana wartość rynkowa',
                  title2: '50 200 zł',
                  description: 'Szacowana wartość rynkowa na dzień 2025-07-01',
                },
                {
                  id: 'elementy-metalowe',
                  title2: '80 200 zł',
                  title: 'Cena w dniu zakupu',
                  description: 'Cena w dniu zakupu na dzień 2020-07-01',
                },
              ]}
              summaryTitle=""
              summaryContent={`  Szacowana wartość rynkowa na dzień ${valuation.date}. Analiza oparta na ${valuation.marketAnalysis.similarVehicles}
                  podobnych pojazdach`}
            />
          </section>

          {/* Wyposażenie pojazdu */}
          <section className="mb-12">
            <SectionTitle
              title="Wyposażenie pojazdu"
              description="Kompletne wyposażenie pojazdu"
              icon="car-ra"
              bgWhite
            />

            {/* Wyposażenie standardowe */}
            <div className="mt-6">
              <div className="mb-4">
                <button
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === 'standard' ? null : 'standard',
                    )
                  }
                  className="flex justify-between items-center p-4 w-full bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                >
                  <h3 className="text-lg font-semibold">
                    Wyposażenie standardowe
                  </h3>
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-600">
                      {equipment.standardEquipment.length} elementów
                    </span>
                    <SVGIcon
                      name={
                        expandedSection === 'standard'
                          ? 'arrow-left'
                          : 'arrow-right'
                      }
                      size="sm"
                    />
                  </div>
                </button>
                {expandedSection === 'standard' && (
                  <div className="mt-4">
                    <InspectionList
                      items={standardEquipmentItems}
                      ariaLabel="Lista wyposażenia standardowego"
                    />
                  </div>
                )}
              </div>

              {/* Pakiety wyposażenia */}
              <div className="mb-4">
                <button
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === 'packages' ? null : 'packages',
                    )
                  }
                  className="flex justify-between items-center p-4 w-full bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
                >
                  <h3 className="text-lg font-semibold">Pakiety wyposażenia</h3>
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-600">
                      {equipment.equipmentPackages.length} pakietów
                    </span>
                    <SVGIcon
                      name={
                        expandedSection === 'packages'
                          ? 'arrow-left'
                          : 'arrow-right'
                      }
                      size="sm"
                    />
                  </div>
                </button>
                {expandedSection === 'packages' && (
                  <div className="mt-4">
                    <InspectionList
                      items={packageItems}
                      ariaLabel="Lista pakietów wyposażenia"
                    />
                    {/* Szczegóły pakietów */}
                    <div className="mt-6 space-y-4">
                      {equipment.equipmentPackages.map((pkg) => (
                        <div
                          key={pkg.id}
                          className="p-4 bg-white rounded-lg border border-gray-200"
                        >
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold">{pkg.name}</h4>
                            <span className="font-bold text-blue-600">
                              {pkg.price}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <strong>Zawiera:</strong>
                            <ul className="mt-2 list-disc list-inside">
                              {pkg.items.map((item, index) => (
                                <li key={index}>
                                  {item.code} - {item.name}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Wyposażenie dodatkowe */}
              <div className="mb-4">
                <button
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === 'additional' ? null : 'additional',
                    )
                  }
                  className="flex justify-between items-center p-4 w-full bg-yellow-50 rounded-lg transition-colors hover:bg-yellow-100"
                >
                  <h3 className="text-lg font-semibold">
                    Wyposażenie dodatkowe
                  </h3>
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-600">
                      {equipment.additionalEquipment.length} elementów
                    </span>
                    <SVGIcon
                      name={
                        expandedSection === 'additional'
                          ? 'arrow-left'
                          : 'arrow-right'
                      }
                      size="sm"
                    />
                  </div>
                </button>
                {expandedSection === 'additional' && (
                  <div className="mt-4">
                    <InspectionList
                      items={additionalEquipmentItems}
                      ariaLabel="Lista wyposażenia dodatkowego"
                    />
                    <div className="p-4 mt-6 bg-white rounded-lg border border-gray-200">
                      <div className="text-center">
                        <h4 className="mb-2 text-lg font-semibold">
                          Sumaryczny koszt wyposażenia dodatkowego
                        </h4>
                        <div className="text-2xl font-bold text-text-primary">
                          {equipment.totalAdditionalCost.toLocaleString()} PLN
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Zastrzeżenia */}
          <section className="mb-12">
            <SectionTitle title="Zastrzeżenia do raportu" bgWhite />
            <div className="mt-6">
              <TextSummaryBox title="Uwaga!">
                <p>
                  Brak wpisów w naszej bazie nie oznacza że samochód nie ma lub
                  nie miał uszkodzeń w przeszłości. Zbierane przez nas
                  informacje pochodzą z różnych źródeł i mimo dokładanych starań
                  mogą nie być kompletne lub zawierać wpisy powtórzone czy
                  zawierające błędy.
                </p>
                <p>
                  Zarówno daty jak i kwoty przedstawiane w raporcie są
                  orientacyjne i nie możemy gwarantować ich pełnej dokładności.
                  Informacje na temat kolizji, wypadków czy innych zdarzeń są
                  zbierane na bieżąco, ale mogą trafiać do baz danych z
                  opóźnieniem.
                </p>
                <p>
                  Z tego powodu serwis udostępnia dane jedynie w celach
                  informacyjnych i poglądowych. Przed zakupem samochodu radzimy
                  przeprowadzić dokładną inspekcję z udziałem specjalisty.
                </p>
              </TextSummaryBox>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default VinStoryReport;
